import React, { useEffect } from "react";
import { companyInfo } from "../../data/mockData";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const Datenschutz = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#f5f5f7] border-b border-[#e5e5e5]">
        <div className="max-w-[800px] mx-auto px-6 py-12">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-[#4a7c59] hover:underline mb-6 transition-colors"
          >
            <ArrowLeft size={16} />
            Zurück zur Startseite
          </button>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold text-[#1d1d1f] tracking-tight"
          >
            Datenschutzerklärung
          </motion.h1>
          <p className="text-[#6e6e73] mt-3 text-sm">
            Zuletzt aktualisiert: Juli 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="max-w-[800px] mx-auto px-6 py-16"
      >
        <div className="prose-custom">
          <Section title="1. Datenschutz auf einen Blick">
            <H3>Allgemeine Hinweise</H3>
            <P>
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen
              Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit
              denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema
              Datenschutz entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
            </P>
            <H3>Datenerfassung auf dieser Website</H3>
            <P>
              <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong>
              <br />
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber:
            </P>
            <P>
              {companyInfo.fullName}
              <br />
              {companyInfo.address}
              <br />
              Telefon: {companyInfo.phone}
              <br />
              E-Mail: {companyInfo.email}
            </P>
            <P>
              <strong>Wie erfassen wir Ihre Daten?</strong>
              <br />
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich
              z.B. um Daten handeln, die Sie in ein Kontaktformular oder das Reservierungsformular eingeben.
              Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch
              unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser,
              Betriebssystem oder Uhrzeit des Seitenaufrufs).
            </P>
            <P>
              <strong>Wofür nutzen wir Ihre Daten?</strong>
              <br />
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu
              gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden. Wenn
              Sie über das Reservierungsformular eine Anfrage stellen, nutzen wir Ihre Kontaktdaten zur
              Bearbeitung Ihrer Anfrage.
            </P>
          </Section>

          <Section title="2. Hosting">
            <P>
              Wir hosten die Inhalte unserer Website bei folgendem Anbieter. Die Server des Hosters befinden
              sich in Deutschland. Der Hoster erhebt in sog. Logfiles folgende Daten, die Ihr Browser
              übermittelt: IP-Adresse, die Adresse der vorher besuchten Website (Referer Anfrage-Header),
              Datum und Uhrzeit der Anfrage, Zeitzonendifferenz zur Greenwich Mean Time, Inhalt der
              Anforderung, HTTP-Statuscode, übertragene Datenmenge, Website, von der die Anforderung kommt
              und Informationen zu Browser und Betriebssystem.
            </P>
            <P>
              Das ist erforderlich, um unsere Website anzuzeigen und die Stabilität und Sicherheit zu
              gewährleisten. Dies entspricht unserem berechtigten Interesse im Sinne des Art. 6 Abs. 1 S. 1
              lit. f DSGVO.
            </P>
          </Section>

          <Section title="3. Allgemeine Hinweise und Pflichtinformationen">
            <H3>Datenschutz</H3>
            <P>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir
              behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen
              Datenschutzvorschriften sowie dieser Datenschutzerklärung.
            </P>
            <P>
              Wenn Sie diese Website benutzen, werden verschiedene personenbezogene Daten erhoben.
              Personenbezogene Daten sind Daten, mit denen Sie persönlich identifiziert werden können. Die
              vorliegende Datenschutzerklärung erläutert, welche Daten wir erheben und wofür wir sie nutzen.
              Sie erläutert auch, wie und zu welchem Zweck das geschieht.
            </P>
            <H3>Hinweis zur verantwortlichen Stelle</H3>
            <P>
              Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
            </P>
            <P>
              {companyInfo.fullName}
              <br />
              {companyInfo.address}
              <br />
              Telefon: {companyInfo.phone}
              <br />
              E-Mail: {companyInfo.email}
            </P>
            <H3>Speicherdauer</H3>
            <P>
              Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde,
              verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung
              entfällt. Wenn Sie ein berechtigtes Löschersuchen geltend machen oder eine Einwilligung zur
              Datenverarbeitung widerrufen, werden Ihre Daten gelöscht, sofern wir keine anderen rechtlich
              zulässigen Gründe für die Speicherung Ihrer personenbezogenen Daten haben.
            </P>
            <H3>Widerruf Ihrer Einwilligung zur Datenverarbeitung</H3>
            <P>
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie
              können eine bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum
              Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
            </P>
            <H3>Recht auf Datenübertragbarkeit</H3>
            <P>
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines
              Vertrags automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen,
              maschinenlesbaren Format aushändigen zu lassen.
            </P>
            <H3>Auskunft, Löschung und Berichtigung</H3>
            <P>
              Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf
              unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und
              Empfänger und den Zweck der Datenverarbeitung und ggf. ein Recht auf Berichtigung oder
              Löschung dieser Daten. Hierzu sowie zu weiteren Fragen zum Thema personenbezogene Daten können
              Sie sich jederzeit an uns wenden.
            </P>
            <H3>Recht auf Einschränkung der Verarbeitung</H3>
            <P>
              Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu
              verlangen. Hierzu können Sie sich jederzeit an uns wenden.
            </P>
          </Section>

          <Section title="4. Datenerfassung auf dieser Website">
            <H3>Anfrage per E-Mail, Telefon oder Formular</H3>
            <P>
              Wenn Sie uns per E-Mail, Telefon, über das Reservierungsformular oder über das Kontaktformular
              kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden personenbezogenen Daten
              (Name, Telefonnummer, E-Mail-Adresse, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei
              uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
            </P>
            <P>
              Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern
              Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung
              vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung
              auf unserem berechtigten Interesse an der effektiven Bearbeitung der an uns gerichteten
              Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1 lit. a
              DSGVO), sofern diese abgefragt wurde.
            </P>
            <P>
              Die von Ihnen an uns per Kontaktanfragen übersandten Daten verbleiben bei uns, bis Sie uns zur
              Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die
              Datenspeicherung entfällt. Zwingende gesetzliche Bestimmungen – insbesondere gesetzliche
              Aufbewahrungsfristen – bleiben unberührt.
            </P>
            <H3>Reservierungsformular</H3>
            <P>
              Wenn Sie über unser Reservierungsformular eine Anfrage stellen, werden folgende Daten erhoben:
              Name/Firma, Telefonnummer, E-Mail-Adresse (optional), gewünschte Bühnenart, Arbeitshöhe,
              Antriebsart, Mietzeit sowie eine optionale Nachricht. Diese Daten werden ausschließlich zur
              Bearbeitung Ihrer Reservierungsanfrage verwendet.
            </P>
          </Section>

          <Section title="5. Cookies">
            <P>
              Unsere Internetseiten verwenden so genannte „Cookies“. Cookies sind kleine Datenpakete und
              richten auf Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer
              einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät
              gespeichert. Session-Cookies werden nach Ende Ihres Besuchs automatisch gelöscht. Permanente
              Cookies bleiben auf Ihrem Endgerät gespeichert, bis Sie diese selbst löschen oder eine
              automatische Löschung durch Ihren Webbrowser erfolgt.
            </P>
            <P>
              Cookies, die zur Durchführung des elektronischen Kommunikationsvorgangs, zur Bereitstellung
              bestimmter, von Ihnen erwünschter Funktionen oder zur Optimierung der Website erforderlich
              sind (notwendige Cookies), werden auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO gespeichert.
              Der Websitebetreiber hat ein berechtigtes Interesse an der Speicherung von notwendigen Cookies
              zur technisch fehlerfreien und optimierten Bereitstellung seiner Dienste.
            </P>
            <P>
              Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden
              und Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle oder
              generell ausschließen sowie das automatische Löschen der Cookies beim Schließen des Browsers
              aktivieren. Bei der Deaktivierung von Cookies kann die Funktionalität dieser Website
              eingeschränkt sein.
            </P>
            <P>
              Weitere Informationen zu unserer Verwendung von Cookies finden Sie in unserer{" "}
              <a href="/cookies" className="text-[#4a7c59] hover:underline font-medium">Cookie-Richtlinie</a>.
            </P>
          </Section>

          <Section title="6. Webanalyse mit PostHog">
            <P>
              Diese Website nutzt – ausschließlich mit Ihrer Einwilligung – den Webanalyse-Dienst
              PostHog, um die Nutzung unserer Website statistisch auszuwerten und unser Angebot zu
              verbessern. Anbieter ist die PostHog Inc., 2261 Market Street #4008, San Francisco,
              CA 94114, USA.
            </P>
            <P>
              Beim Einsatz von PostHog werden Nutzungsdaten (u. a. aufgerufene Seiten, Klicks, ungefähre
              Herkunft, Datum und Uhrzeit des Zugriffs sowie Geräte- und Browserinformationen) verarbeitet
              und in unserem Auftrag ausgewertet. Dabei können Cookies bzw. vergleichbare Technologien
              eingesetzt werden. Die Daten werden auf Servern in der Europäischen Union (Frankfurt am
              Main, Deutschland) gespeichert und verarbeitet; eine Übermittlung in die USA findet dabei
              nicht statt.
            </P>
            <P>
              Die Verarbeitung erfolgt ausschließlich auf Grundlage Ihrer Einwilligung gemäß
              Art. 6 Abs. 1 lit. a DSGVO in Verbindung mit § 25 Abs. 1 TDDDG. Ohne Ihre Einwilligung wird
              PostHog nicht geladen und es werden keine Analyse-Daten erhoben. Die Einwilligung erteilen
              Sie über unseren Cookie-Hinweis („Alle akzeptieren“).
            </P>
            <P>
              Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen. Nutzen Sie
              dazu den Link <strong>„Cookie-Einstellungen“</strong> im Seitenfuß und wählen Sie
              „Nur Notwendige“. Ab dem Widerruf werden keine weiteren Analyse-Daten mehr erhoben.
            </P>
          </Section>

          <Section title="7. Ihre Rechte">
            <P>
              Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen
              Daten:
            </P>
            <ul className="list-disc list-inside space-y-2 text-[#424245] text-[15px] leading-relaxed mb-6">
              <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
              <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
              <li>Recht auf Löschung (Art. 17 DSGVO)</li>
              <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Recht auf Widerspruch (Art. 21 DSGVO)</li>
            </ul>
            <P>
              Sie haben zudem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung
              Ihrer personenbezogenen Daten durch uns zu beschweren.
            </P>
          </Section>
        </div>
      </motion.div>
    </div>
  );
};

// Reusable sub-components
const Section = ({ title, children }) => (
  <div className="mb-12">
    <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight mb-6">{title}</h2>
    {children}
  </div>
);

const H3 = ({ children }) => (
  <h3 className="text-lg font-semibold text-[#1d1d1f] mt-6 mb-3">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-[#424245] text-[15px] leading-relaxed mb-4">{children}</p>
);

export default Datenschutz;
